// 2024 weapon range, reach, Loading and Heavy rules (SRD 5.2.1 "Weapons" and "Making an Attack"), shared by the
// attack resolver and the server's attack authorization. Pure: callers measure distances.
// - Melee: the target must be within reach (5 feet, 10 with Reach).
// - Ranged and Thrown: Disadvantage beyond normal range; no attack beyond long range. A Melee weapon with Thrown can
//   be thrown at a target beyond reach, which makes it a ranged attack.
// - Loading: one shot per action, Bonus Action or Reaction, whatever the number of attacks.
// - Heavy: Disadvantage with a Melee weapon if Strength is below 13, or a Ranged weapon if Dexterity is below 13.
//   The weapon's category decides, not how it is used: a thrown Melee weapon still checks Strength. Size plays no part
//   (the 2014 Small-creature rule is gone), and Versatile changes nothing.
const propertyNames = (weapon) => (Array.isArray(weapon?.properties) ? weapon.properties : [])
  .map((property) => String(property?.name || property).trim().toLowerCase());

export const weaponHasProperty = (weapon, name) => propertyNames(weapon).some((property) => property.split(/\s|\(/)[0] === name);

const category = (weapon) => [weapon?.category, weapon?.weaponCategory, weapon?.weaponType, weapon?.type]
  .map((value) => String(value || '').trim().toLowerCase()).find(Boolean) || '';
export const isRangedWeapon = (weapon) => category(weapon).includes('ranged');

/** Normal and long range in feet, from "ammunition (150/600)" or "thrown (20/60)", or range fields; null if none. */
export const weaponRangeFeet = (weapon) => {
  for (const text of [...propertyNames(weapon), String(weapon?.range || '')]) {
    const match = /(\d+)\s*\/\s*(\d+)/.exec(text);
    if (match) return { normal: Number(match[1]), long: Number(match[2]) };
  }
  const normal = Number(weapon?.normalRange), long = Number(weapon?.longRange);
  return Number.isFinite(normal) && normal > 0 ? { normal, long: Number.isFinite(long) && long >= normal ? long : normal } : null;
};

export const weaponReachFeet = (weapon) => (weaponHasProperty(weapon, 'reach') ? 10 : 5);

export const LONG_RANGE_SOURCE = 'Beyond normal range';
export const ADJACENT_ENEMY_SOURCE = 'Ranged attack with an enemy within 5 feet';

/**
 * How a weapon (or an Unarmed Strike) can attack a target `distanceFeet` away. Returns { mode: 'melee' | 'ranged' |
 * 'thrown', allowed, longRange, reason }. An unknown distance (no map) allows the attack in its usual mode.
 */
export const resolveWeaponAttackRange = ({ weapon, distanceFeet, unarmed = false }) => {
  const known = Number.isFinite(distanceFeet);
  const name = weapon?.name || 'This weapon';
  const ranged = (range, mode) => {
    if (!range) return { mode, allowed: !known, longRange: false, reason: `${name} has no range listed.` };
    if (known && distanceFeet > range.long) return { mode, allowed: false, longRange: false, reason: `${name} can't reach a target beyond ${range.long} feet.` };
    return { mode, allowed: true, longRange: known && distanceFeet > range.normal, reason: null };
  };
  if (!unarmed && weapon && isRangedWeapon(weapon)) return ranged(weaponRangeFeet(weapon), 'ranged');
  const reach = unarmed || !weapon ? 5 : weaponReachFeet(weapon);
  if (!known || distanceFeet <= reach) return { mode: 'melee', allowed: true, longRange: false, reason: null };
  if (!unarmed && weapon && weaponHasProperty(weapon, 'thrown')) return ranged(weaponRangeFeet(weapon), 'thrown');
  return { mode: 'melee', allowed: false, longRange: false, reason: `${unarmed || !weapon ? 'Unarmed Strike' : name} has a reach of ${reach} feet.` };
};

/** The Heavy property's Disadvantage source for this weapon and these ability scores, or null. */
export const heavyWeaponDisadvantage = ({ weapon, strength, dexterity }) => {
  if (!weaponHasProperty(weapon, 'heavy')) return null;
  if (isRangedWeapon(weapon)) return Number(dexterity) < 13 ? 'Heavy weapon: Dexterity below 13' : null;
  return Number(strength) < 13 ? 'Heavy weapon: Strength below 13' : null;
};

/** Loading: a weapon already fired in this action cannot fire again in it. */
export const loadingShotSpent = (attackAction, weaponInstanceId) => (Array.isArray(attackAction?.loadingShots)
  ? attackAction.loadingShots : []).includes(String(weaponInstanceId));

/**
 * Reach and range of a monster attack action, from its stat-block text: 2024 "Melee Attack Roll: +4, reach 5 ft." /
 * "Ranged Attack Roll: +4, range 150/600 ft." / "Melee or Ranged Attack Roll: … reach 5 ft. or range 30/120 ft.",
 * and the 2014 "Melee Weapon Attack: … reach 5 ft." wording. Reach defaults to 5 feet.
 */
export const monsterAttackRangeFeet = (action) => {
  const head = String(action?.desc || '').split(/\bHit:/i)[0];
  const kind = `${head} ${action?.attack_type || ''}`;
  const reachMatch = /reach\s+(\d+)\s*(?:ft|feet)/i.exec(head);
  const rangeMatch = /range\s+(\d+)(?:\s*\/\s*(\d+))?\s*(?:ft|feet)/i.exec(head);
  return { melee: /\bmelee\b/i.test(kind), ranged: /\branged\b/i.test(kind), reach: reachMatch ? Number(reachMatch[1]) : 5,
    range: rangeMatch ? { normal: Number(rangeMatch[1]), long: Number(rangeMatch[2] || rangeMatch[1]) } : null };
};

/**
 * Whether a monster attack action reaches a target `distanceFeet` away. Same result shape as resolveWeaponAttackRange;
 * an action whose text names neither melee nor ranged is never blocked.
 */
export const resolveMonsterAttackRange = ({ action, distanceFeet }) => {
  const { melee, ranged: rangedAttack, reach, range } = monsterAttackRangeFeet(action);
  const name = action?.name || 'This attack';
  const known = Number.isFinite(distanceFeet);
  if (!melee && !rangedAttack) return { mode: 'melee', allowed: true, longRange: false, reason: null };
  if (melee && (!known || distanceFeet <= reach)) return { mode: 'melee', allowed: true, longRange: false, reason: null };
  if (rangedAttack) {
    if (known && range && distanceFeet > range.long) return { mode: 'ranged', allowed: false, longRange: false, reason: `${name} can't reach a target beyond ${range.long} feet.` };
    return { mode: 'ranged', allowed: true, longRange: Boolean(known && range && distanceFeet > range.normal), reason: null };
  }
  return { mode: 'melee', allowed: false, longRange: false, reason: `${name} has a reach of ${reach} feet.` };
};
